/**
 * SubscriptionItem Component
 * Individual subscription card with password management
 */

import React, { useState } from 'react';
import { Subscription } from '../types/SubscriptionTypes';
import { useSubscriptionItem } from '../hooks/useSubscriptions';
import { PasswordManager } from './PasswordManager';
import './SubscriptionItem.css';

interface SubscriptionItemProps {
  subscription: Subscription;
  onSubscriptionUpdate?: (subscriptionId: string) => void;
}

export const SubscriptionItem: React.FC<SubscriptionItemProps> = ({
  subscription,
  onSubscriptionUpdate
}) => {
  const { isExpanded, toggleExpanded, passwordInfo, showPassword, setShowPassword } =
    useSubscriptionItem(subscription.id);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'status-active';
      case 'inactive':
        return 'status-inactive';
      case 'pending-setup':
        return 'status-pending';
      default:
        return 'status-unknown';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'inactive':
        return 'Inactive';
      case 'pending-setup':
        return 'Pending Setup';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className={`subscription-item ${isExpanded ? 'expanded' : ''}`}>
      <div className="subscription-item-header">
        <button
          className="subscription-item-trigger"
          onClick={toggleExpanded}
          aria-expanded={isExpanded}
          aria-controls={`subscription-content-${subscription.id}`}
        >
          <div className="subscription-info">
            <h4 className="subscription-name">{subscription.name}</h4>
            {subscription.setupTime && <span className="setup-time">{subscription.setupTime} to setup</span>}
            {subscription.cost && <span className="cost-badge">{subscription.cost}</span>}
          </div>

          <div className="subscription-actions">
            <span className={`status-badge ${getStatusColor(subscription.status)}`}>
              {getStatusLabel(subscription.status)}
            </span>
            {subscription.signInUrl && (
              <a
                href={subscription.signInUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="sign-in-link"
                title="Sign in to this service"
              >
                Sign In →
              </a>
            )}
            <span className={`expand-icon ${isExpanded ? 'rotated' : ''}`}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor">
                <polyline points="4 6 8 10 12 6"></polyline>
              </svg>
            </span>
          </div>
        </button>
      </div>

      {isExpanded && (
        <div className="subscription-item-content" id={`subscription-content-${subscription.id}`}>
          <div className="subscription-details">
            {subscription.description && (
              <div className="subscription-description">
                <p>{subscription.description}</p>
              </div>
            )}

            {subscription.setupInstructions && (
              <div className="subscription-instructions">
                <h5>Setup Instructions</h5>
                <p>{subscription.setupInstructions}</p>
              </div>
            )}

            <div className="subscription-password-section">
              <div className="password-section-header">
                <h5>Credentials</h5>
                {passwordInfo && (
                  <div className="rotation-info">
                    {passwordInfo.nextRotation && (
                      <span className="next-rotation">
                        Next rotation: {new Date(passwordInfo.nextRotation).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <PasswordManager
                subscriptionId={subscription.id}
                lastRotated={passwordInfo?.lastRotated}
                nextRotation={passwordInfo?.nextRotation}
                onPasswordUpdated={() => {
                  if (onSubscriptionUpdate) {
                    onSubscriptionUpdate(subscription.id);
                  }
                }}
              />
            </div>

            {subscription.signInUrl && (
              <div className="subscription-footer">
                <a
                  href={subscription.signInUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sign-in-button"
                >
                  Open Sign In Page
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionItem;
